import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Search, Eye, ThumbsUp, Youtube, RefreshCw, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The 10 major DAWs — powers the DAW Hub category + its sub-chips
const DAW_HUB_CHIPS = [
  { id: 'all', label: 'All DAWs' },
  { id: 'ableton', label: 'Ableton Live', keywords: ['ableton'] },
  { id: 'flstudio', label: 'FL Studio', keywords: ['fl studio', 'flstudio', 'fruity loops'] },
  { id: 'logic', label: 'Logic Pro', keywords: ['logic pro'] },
  { id: 'protools', label: 'Pro Tools', keywords: ['pro tools'] },
  { id: 'cubase', label: 'Cubase', keywords: ['cubase'] },
  { id: 'studioone', label: 'Studio One', keywords: ['studio one'] },
  { id: 'reaper', label: 'Reaper', keywords: ['reaper'] },
  { id: 'bitwig', label: 'Bitwig', keywords: ['bitwig'] },
  { id: 'reason', label: 'Reason', keywords: ['reason studios', 'propellerhead', 'reason 1', 'reason daw', 'with reason'] },
  { id: 'garageband', label: 'GarageBand', keywords: ['garageband', 'garage band'] },
];
const DAW_HUB_ALL_KEYWORDS = DAW_HUB_CHIPS.filter(c => c.keywords).flatMap(c => c.keywords);

// Producer interest categories with keywords for filtering
const PRODUCER_INTEREST_CATEGORIES = [
  { id: 'all', name: 'All Videos' },
  { id: 'mixing', name: 'Mixing', keywords: ['mix', 'mixing', 'master', 'mastering', 'EQ', 'compression', 'limiter', 'loudness'] },
  { id: 'sound-design', name: 'Sound Design', keywords: ['sound design', 'synth', 'synthesis', 'wavetable', 'FM', 'granular', 'patch', 'preset'] },
  { id: 'daw', name: 'DAW Tips', keywords: ['ableton', 'fl studio', 'logic', 'pro tools', 'cubase', 'bitwig', 'studio one', 'DAW', 'workflow'] },
  { id: 'daw-hub', name: 'DAW Hub', keywords: DAW_HUB_ALL_KEYWORDS },
  { id: 'daw-reviews', name: 'DAW Reviews', keywords: ['DAW review', 'DAW comparison', 'best DAW', 'ableton review', 'fl studio review', 'logic pro review', 'cubase review', 'bitwig review', 'studio one review', 'reaper review', 'reason review', 'garageband review', 'DAW vs', 'which DAW'] },
  { id: 'beat-making', name: 'Beat Making', keywords: ['beat', 'drum', 'rhythm', 'groove', 'percussion', 'hi-hat', 'kick', 'snare', 'pattern'] },
  { id: 'synth', name: 'Synth', keywords: ['serum', 'massive', 'sylenth', 'omnisphere', 'vital', 'pigments', 'phase plant', 'oscillator', 'LFO', 'filter'] },
  { id: 'theory', name: 'Music Theory', keywords: ['theory', 'chord', 'scale', 'harmony', 'melody', 'progression', 'key', 'mode', 'arrangement'] },
  { id: 'industry', name: 'Industry', keywords: ['industry', 'career', 'label', 'release', 'promotion', 'marketing', 'streaming', 'royalt'] }
];

const VideosBoardTab = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInterest, setSelectedInterest] = useState('all');
  const [selectedDaw, setSelectedDaw] = useState('all');
  const [sortBy, setSortBy] = useState('trending');
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/videos?limit=500`);
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const handleVideoClick = (video) => {
    // Track view
    try {
      fetch(`${BACKEND_URL}/api/videos/${video.id}/view`, {
        method: 'POST'
      }).catch(err => console.log('Failed to track view:', err));
    } catch (error) {
      console.log('Failed to track video view:', error);
    }
    
    // Open modal
    setSelectedVideo(video);
    setShowYouTubeModal(true);
  };

  const getVideoThumbnail = (video) => {
    // Try multiple sources for thumbnail
    if (video.thumbnail && !video.thumbnail.includes('undefined')) {
      return video.thumbnail;
    }
    if (video.youtube_id) {
      return `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`;
    }
    return 'https://via.placeholder.com/480x270/1a1a2e/9333ea?text=Video';
  };

  const getFilteredVideos = () => {
    let filtered = videos;
    
    // Apply interest category filter
    if (selectedInterest !== 'all') {
      let keywords = null;
      if (selectedInterest === 'daw-hub' && selectedDaw !== 'all') {
        const chip = DAW_HUB_CHIPS.find(c => c.id === selectedDaw);
        keywords = chip?.keywords || null;
      } else {
        const category = PRODUCER_INTEREST_CATEGORIES.find(c => c.id === selectedInterest);
        keywords = category?.keywords || null;
      }
      if (keywords) {
        filtered = filtered.filter(video => {
          const searchText = `${video.title || ''} ${video.description || ''} ${video.category || ''}`.toLowerCase();
          return keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
        });
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(video => {
        const searchText = `${video.title || ''} ${video.description || ''}`.toLowerCase();
        return searchText.includes(term);
      });
    }
    
    // Apply sorting
    if (sortBy === 'newest') {
      filtered = [...filtered].sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
    } else if (sortBy === 'most-viewed') {
      filtered = [...filtered].sort((a, b) => (b.views || 0) - (a.views || 0));
    } else if (sortBy === 'most-liked') {
      filtered = [...filtered].sort((a, b) => (b.likes || 0) - (a.likes || 0));
    } else if (sortBy === 'a-z') {
      filtered = [...filtered].sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'z-a') {
      filtered = [...filtered].sort((a, b) => (b.title || '').localeCompare(a.title || '', undefined, { sensitivity: 'base' }));
    }
    
    return filtered;
  };

  const filteredVideos = getFilteredVideos();

  return (
    <div className="video-board-tab">
      {/* Filter Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Interest Category Dropdown - Compact */}
        <select
          value={selectedInterest}
          onChange={(e) => { setSelectedInterest(e.target.value); setSelectedDaw('all'); }}
          style={{
            padding: '0.75rem 1rem',
            background: 'rgba(30, 20, 50, 0.5)',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '0.875rem',
            cursor: 'pointer',
            minWidth: '120px',
            maxWidth: '140px'
          }}
        >
          {PRODUCER_INTEREST_CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        {/* Search Input - Takes more space */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '1rem', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(255,255,255,0.4)'
          }} />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              background: 'rgba(30, 20, 50, 0.5)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Sort Dropdown */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: 'rgba(30, 20, 50, 0.5)',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '0.875rem'
          }}
        >
          <option value="trending">🔥 Trending</option>
          <option value="newest">📅 Newest</option>
          <option value="most-viewed">👁️ Most Viewed</option>
          <option value="most-liked">👍 Most Liked</option>
          <option value="a-z">🔤 A-Z</option>
          <option value="z-a">🔤 Z-A</option>
        </select>

        {/* Refresh Button */}
        <button
          onClick={loadVideos}
          disabled={loading}
          style={{
            padding: '0.75rem',
            borderRadius: '10px',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            background: 'rgba(30, 20, 50, 0.5)',
            color: loading ? 'rgba(255,255,255,0.4)' : '#c4b5fd',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* DAW Hub sub-chips — drill into a specific DAW */}
      {selectedInterest === 'daw-hub' && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }} data-testid="daw-hub-chips">
          {DAW_HUB_CHIPS.map(chip => (
            <button
              key={chip.id}
              onClick={() => setSelectedDaw(chip.id)}
              data-testid={`daw-chip-${chip.id}`}
              style={{
                padding: '0.4rem 0.85rem',
                borderRadius: '999px',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                border: selectedDaw === chip.id ? '1px solid #a855f7' : '1px solid rgba(147, 51, 234, 0.3)',
                background: selectedDaw === chip.id ? 'rgba(147, 51, 234, 0.35)' : 'rgba(30, 20, 50, 0.5)',
                color: selectedDaw === chip.id ? '#fff' : '#c4b5fd',
                transition: 'background-color 0.15s, border-color 0.15s'
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {/* Filter Info */}
      <div style={{ 
        background: 'rgba(147, 51, 234, 0.1)', 
        border: '1px solid rgba(147, 51, 234, 0.2)',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        fontSize: '0.8rem',
        color: 'rgba(255,255,255,0.6)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>🎬 Showing {filteredVideos.length} videos • Powered by YouTube</span>
        {selectedInterest !== 'all' && (
          <span style={{ color: '#fbbf24' }}>
            Filter: {PRODUCER_INTEREST_CATEGORIES.find(c => c.id === selectedInterest)?.name}
          </span>
        )}
      </div>

      {/* Video Grid */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid rgba(147,51,234,0.3)', borderTopColor: '#9333ea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading videos...</p>
          </div>
        </div>
      ) : filteredVideos.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              onClick={() => handleVideoClick(video)}
              style={{
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(147, 51, 234, 0.2)',
                borderRadius: '12px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                <img
                  src={getVideoThumbnail(video)}
                  alt={video.title}
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }}
                  onError={(e) => { 
                    // Fallback to YouTube thumbnail using video ID
                    if (video.youtube_id && !e.target.src.includes('mqdefault')) {
                      e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`;
                    } else {
                      e.target.src = 'https://via.placeholder.com/480x270/1a1a2e/9333ea?text=Video';
                    }
                  }}
                />
                {/* Play overlay */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '50%',
                  padding: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  <Play size={24} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                </div>
                {/* Duration badge */}
                {video.duration && video.duration !== '0:00' && (
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.75rem'
                  }}>
                    {video.duration}
                  </div>
                )}
              </div>
              <div style={{ padding: '1rem' }}>
                <h4 style={{ 
                  color: '#fff', 
                  fontSize: '0.9rem', 
                  fontWeight: '600', 
                  marginBottom: '0.5rem', 
                  lineHeight: '1.3',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {video.title}
                </h4>
                {video.channel_name && (
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    {video.channel_name}
                  </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Eye size={12} /> {formatNumber(video.views)}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ThumbsUp size={12} /> {formatNumber(video.likes)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Youtube size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '1rem' }} />
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
            {searchTerm || selectedInterest !== 'all' 
              ? 'No videos match your filters' 
              : 'No videos available'}
          </p>
          {(searchTerm || selectedInterest !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setSelectedInterest('all'); }}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid #9333ea',
                background: 'transparent',
                color: '#c4b5fd',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* YouTube Modal - Using Portal */}
      {showYouTubeModal && selectedVideo && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => {
            setShowYouTubeModal(false);
            setSelectedVideo(null);
          }}
        >
          <div 
            style={{
              width: '90%',
              maxWidth: '900px',
              background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98))',
              borderRadius: '16px',
              padding: '1rem',
              overflow: 'hidden',
              position: 'relative',
              border: '2px solid rgba(147, 51, 234, 0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(147, 51, 234, 0.3)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                margin: 0,
                background: 'linear-gradient(135deg, #fbbf24 0%, #9333ea 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                YouTube on Producer's Corner
              </h3>
              <button
                onClick={() => {
                  setShowYouTubeModal(false);
                  setSelectedVideo(null);
                }}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#ef4444',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </div>

            {/* Video Info */}
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#fff',
                margin: '0 0 0.25rem 0'
              }}>
                {selectedVideo.title}
              </p>
              {selectedVideo.channel_name && (
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  margin: 0
                }}>
                  {selectedVideo.channel_name}
                </p>
              )}
            </div>

            {/* Video Player Container - 16:9 aspect ratio */}
            <div style={{ 
              position: 'relative', 
              width: '100%',
              paddingTop: '56.25%',
              background: '#000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1&controls=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>

            {/* Footer */}
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <p style={{
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.5)',
                margin: 0
              }}>
                🎵 Powered by YouTube • Keeping you in Producer's Corner
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VideosBoardTab;
